import React, { useCallback, useEffect, useRef } from "react";
import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";

export enum ZoomDirection {
    in = "in",
    out = "out",
}
export interface ZoomableSlideProps {
    index: number;
    currentSlideIndex: number;
    onZoom: (direction: ZoomDirection) => void;
    onZoomStateChange: (isZoomed: boolean) => void;
    onClick: () => void;
    children: React.ReactNode;
}

const LOG = (...args: unknown[]): void =>
    console.warn("[ZoomableSlide]", new Date().toISOString().slice(11, 23), ...args);

export function ZoomableSlide({
    index,
    currentSlideIndex,
    onZoom,
    onZoomStateChange,
    onClick,
    children,
}: ZoomableSlideProps): React.ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const quickPinchZoomRef = useRef<QuickPinchZoom>(null);
    const zoomInEventFired = useRef(false);
    const isZoomed = useRef(false);
    const prevScale = useRef(1);
    const tapTimeoutRef = useRef<number | null>(null);

    const cancelPendingTap = useCallback(() => {
        if (tapTimeoutRef.current !== null) {
            LOG(`[slide ${index}] cancelPendingTap — clearing timeout`);
            window.clearTimeout(tapTimeoutRef.current);
            tapTimeoutRef.current = null;
        }
    }, [index]);

    const handleClick = useCallback(() => {
        LOG(`[slide ${index}] handleClick — tapTimeoutRef=${tapTimeoutRef.current}`);
        if (tapTimeoutRef.current !== null) {
            // Second click within the double-tap window — part of a double-tap-to-zoom.
            LOG(`[slide ${index}] handleClick — treating as double-tap, cancelling`);
            cancelPendingTap();
            return;
        }
        LOG(`[slide ${index}] handleClick — scheduling onClick() in 300ms`);
        tapTimeoutRef.current = window.setTimeout(() => {
            LOG(`[slide ${index}] handleClick — 300ms elapsed, firing onClick()`);
            tapTimeoutRef.current = null;
            onClick();
        }, 300);
    }, [index, cancelPendingTap, onClick]);

    const resetZoom = useCallback(() => {
        const wrapper = containerRef.current;
        const currentZoom = quickPinchZoomRef.current;
        LOG(`[slide ${index}] resetZoom — isZoomed.current=${isZoomed.current}`);
        if (wrapper && currentZoom) {
            const { width, height } = wrapper.getBoundingClientRect();
            // reset inactive slide back to centre
            currentZoom.alignCenter({ x: width / 2, y: height / 2, scale: 1 });
            zoomInEventFired.current = false;
            if (isZoomed.current) {
                LOG(`[slide ${index}] resetZoom — calling onZoomStateChange(false)`);
                isZoomed.current = false;
                onZoomStateChange(false);
            }
            prevScale.current = 1;
        }
    }, [index, onZoomStateChange]);

    const onUpdate = useCallback(
        ({ x, y, scale }: { x: number; y: number; scale: number }) => {
            const wrapper = containerRef.current;
            if (wrapper) {
                // x/y are NaN when QuickPinchZoom can't determine the element's bounding
                // rect — this happens on Safari when the slide is mid-transition animation.
                // Skip the update entirely: applying a NaN transform is a no-op anyway,
                // and we must not let the phantom scale value change the zoom state.
                if (isNaN(x) || isNaN(y)) {
                    LOG(
                        `[slide ${index}] onUpdate — SKIPPING NaN coords (scale=${scale.toFixed(
                            4
                        )})`
                    );
                    return;
                }

                wrapper.style.setProperty("transform", make3dTransformValue({ x, y, scale }));
                const direction =
                    scale > prevScale.current
                        ? ZoomDirection.in
                        : scale < prevScale.current
                        ? ZoomDirection.out
                        : null;

                if (direction === ZoomDirection.in && !zoomInEventFired.current) {
                    zoomInEventFired.current = true;
                    onZoom(ZoomDirection.in);
                } else if (direction === ZoomDirection.out && zoomInEventFired.current) {
                    zoomInEventFired.current = false;
                    onZoom(ZoomDirection.out);
                }

                const nowZoomed = scale > 1;
                if (nowZoomed !== isZoomed.current) {
                    LOG(
                        `[slide ${index}] onUpdate — zoom state CHANGED: ${
                            isZoomed.current
                        } → ${nowZoomed} (scale=${scale.toFixed(4)}, x=${x.toFixed(
                            1
                        )}, y=${y.toFixed(1)})`
                    );
                    isZoomed.current = nowZoomed;
                    onZoomStateChange(nowZoomed);
                } else if (scale !== prevScale.current) {
                    LOG(
                        `[slide ${index}] onUpdate — scale=${scale.toFixed(4)} x=${x.toFixed(
                            1
                        )} y=${y.toFixed(1)} (no zoom state change)`
                    );
                }

                prevScale.current = scale;
            }
        },
        [index, onZoom, onZoomStateChange]
    );

    useEffect(() => {
        LOG(
            `[slide ${index}] useEffect[currentSlideIndex=${currentSlideIndex}] — index ${
                index === currentSlideIndex ? "IS" : "is NOT"
            } active`
        );
        if (index !== currentSlideIndex) {
            cancelPendingTap();
            resetZoom();
        }
    }, [currentSlideIndex, index, cancelPendingTap, resetZoom]);

    useEffect(() => {
        LOG(`[slide ${index}] mounted`);
        return () => {
            LOG(`[slide ${index}] unmount cleanup — cancelling pending tap`);
            cancelPendingTap();
        };
    }, [index, cancelPendingTap]);

    return (
        <QuickPinchZoom
            ref={quickPinchZoomRef}
            onUpdate={onUpdate}
            onDoubleTap={cancelPendingTap}
            draggableUnZoomed={false}
            maxZoom={3}
            doubleTapZoomOutOnMaxScale
            inertia={false}
        >
            <div ref={containerRef} onClick={handleClick} role="none">
                {children}
            </div>
        </QuickPinchZoom>
    );
}
